import axiosInstance from 'shared/services/api/axiosInstance';
import { isDemoTeamActive } from 'shared/utils/demoTeam';

// In the shared demo workspace, "Start healing" is a UI-only simulation — we
// never hit the backend (writes are blocked there anyway). We mint a fake
// in-progress session, remember it, and serve it back to the detail poller so
// the flow feels real without touching a real IDE. Cleared on reload.
let _demoSeq = 0;
const _demoSessions = new Map();

function makeDemoSession({ source, custom_title, custom_description, custom_root_cause, custom_suggested_fix, extra_instructions }) {
  _demoSeq += 1;
  const id = `demo-${_demoSeq}`;
  const now = new Date().toISOString();
  const slug = String(custom_title || 'issue').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40);
  const session = {
    id,
    team_id: null,
    source: source || 'cursor',
    status: 'branch_ready',
    custom_title: custom_title || 'Custom issue',
    custom_description: custom_description || null,
    custom_root_cause: custom_root_cause || null,
    custom_suggested_fix: custom_suggested_fix || null,
    extra_instructions: extra_instructions || null,
    branch_name: `trodo/heal/${slug || 'fix'}`,
    repo_full_name: 'cadence-labs/cadence-agents',
    pr_number: null,
    pr_url: null,
    change_description:
      '## Demo heal\n\nThis is a simulated heal in the demo workspace: no real IDE call was made.\n\n' +
      'In a live workspace the connected agent (Cursor / Claude Code) would clone the repo, ' +
      'implement the fix on this branch, and open a pull request for review.',
    triggered_at: now,
    branch_pushed_at: now,
    pr_opened_at: null,
    created_at: now,
    updated_at: now,
    _demo: true,
  };
  _demoSessions.set(id, session);
  return session;
}

export async function triggerHeal(teamId, {
  source,
  issue_id = null,
  custom_title = null,
  custom_description = null,
  custom_root_cause = null,
  custom_suggested_fix = null,
  extra_instructions = null,
}) {
  if (isDemoTeamActive()) {
    // No backend call in the demo — simulate the heal locally.
    return makeDemoSession({ source, custom_title, custom_description, custom_root_cause, custom_suggested_fix, extra_instructions });
  }
  const { data } = await axiosInstance.post('/heal/trigger', {
    teamId,
    source,
    issue_id,
    custom_title,
    custom_description,
    custom_root_cause,
    custom_suggested_fix,
    extra_instructions,
  });
  return data;
}

export async function listHealSessions(teamId, { limit = 20, offset = 0 } = {}) {
  const { data } = await axiosInstance.get('/heal/sessions', {
    params: { teamId, limit, offset },
  });
  return data?.sessions || [];
}

/**
 * The GitHub compare URL that pre-fills a PR for a session's branch.
 *
 * The agent stops at a branch by design and never opens a PR — this is the
 * link that lets the user do it, which is why it lives next to the branch
 * rather than anywhere else.
 */
export async function getCompareUrl(teamId, sessionId, { base = 'main' } = {}) {
  const { data } = await axiosInstance.get(`/heal/sessions/${sessionId}/compare-url`, {
    params: { teamId, base },
  });
  return data?.url || null;
}

/** Which coding agents this team can actually heal with. */
export async function getConnectedAgents(teamId) {
  if (isDemoTeamActive()) return { cursor: true, claudecode: false };
  const [cursor, claudecode] = await Promise.all([
    axiosInstance.get('/integrations/cursor', { params: { teamId } })
      .then((r) => r.data?.connected === true).catch(() => false),
    axiosInstance.get('/integrations/claudecode', { params: { teamId } })
      .then((r) => r.data?.connected === true).catch(() => false),
  ]);
  return { cursor, claudecode };
}

export async function getHealSession(teamId, id) {
  // Serve simulated demo sessions from memory so the detail poller works.
  if (_demoSessions.has(id)) return _demoSessions.get(id);
  const { data } = await axiosInstance.get(`/heal/sessions/${id}`, {
    params: { teamId },
  });
  return data;
}
